import CommandPalette, { type CommandPaletteItem } from "@galaxy-io/dls/navigation/CommandPalette";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useRouter } from "@/app/navigation";
import { apiClient, unwrap } from "@/lib/api-client";

export function CommandMenu({
  open,
  onOpenChange,
  isAdmin,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isAdmin: boolean;
}) {
  const router = useRouter();
  const [term, setTerm] = useState("");
  const people = useQuery({
    queryKey: ["search-people", term],
    enabled: open && !!term,
    queryFn: ({ signal }) =>
      unwrap(apiClient().GET("/people", { params: { query: { q: term } }, signal })),
    retry: false,
  });
  const companies = useQuery({
    queryKey: ["search-companies", term],
    enabled: open && !!term,
    queryFn: ({ signal }) =>
      unwrap(apiClient().GET("/companies", { params: { query: { q: term } }, signal })),
    retry: false,
  });
  const pages = [
    ["Signals", "/"],
    ["People", "/people"],
    ["Companies", "/companies"],
    ["Integrations", "/integrations"],
    ["Settings", "/settings/preferences"],
    ["Workflows · Library", "/workflows"],
    ["Workflows · Runs", "/workflows?view=history"],
    ["Workflows · Schedules", "/workflows?view=schedules"],
    ["Workflows · Deliveries", "/workflows?view=history&kind=delivery"],
    ["Observability", "/observability"],
    ["Exclusions", "/exclusions"],
    ["Knowledge Center", "/knowledge"],
    ["Q&A", "/qa"],
    ["Dream List", "/dream-list"],
    ["Product Requests", "/product-requests"],
    ["Tags", "/tags"],
    ["Console", "/query"],
    ...(isAdmin ? [["Workspace", "/settings/members"]] : []),
  ]
    .filter(([label]) => label.toLowerCase().includes(term.toLowerCase()))
    .map(([label, href]) => ({ label, href, group: "Pages" }));
  function go(href: string) {
    if (!window.dispatchEvent(new Event("query:navigate", { cancelable: true }))) {
      onOpenChange(true);
      return;
    }
    setTerm("");
    router.push(href);
  }
  const items: CommandPaletteItem[] = pages.map((item) => ({
    id: item.href,
    label: item.label,
    group: item.group,
    onSelect: () => go(item.href),
  }));
  if (term) {
    const groups = [
      {
        name: "People",
        pending: people.isFetching,
        error: people.error,
        items: (people.data?.items ?? []).map((p) => ({
          label: p.name ? `${p.name} · ${p.login}` : p.login,
          href: `/people?personId=${p.id}`,
        })),
      },
      {
        name: "Companies",
        pending: companies.isFetching,
        error: companies.error,
        items: (companies.data?.items ?? []).map((c) => ({
          label: c.name,
          href: `/companies?companyId=${c.id}`,
        })),
      },
    ];
    for (const group of groups) {
      if (group.items.length)
        items.push(
          ...group.items.map((item) => ({
            id: item.href,
            label: item.label,
            group: group.name,
            onSelect: () => go(item.href),
          })),
        );
      else
        items.push({
          id: `status-${group.name}`,
          group: group.name,
          isDisabled: true,
          onSelect: () => {},
          label: group.error
            ? `Could not search ${group.name.toLowerCase()}.`
            : group.pending
              ? "Searching…"
              : `No matching ${group.name.toLowerCase()}.`,
        });
    }
  }
  return (
    <CommandPalette
      isOpen={open}
      onOpenChange={onOpenChange}
      ariaLabel="Jump to…"
      placeholder="Search your workspace…"
      shouldBindHotKey
      items={items}
      onSearch={(value) => setTerm(value.trim())}
      isLoading={!!term && (people.isFetching || companies.isFetching)}
    />
  );
}
